import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, OnDestroy, Output, Directive } from '@angular/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TRANSLATIONS } from './translations';
import { TranslationConfig } from '../../models/types';

@Directive()
@Injectable()
export class TranslateService implements OnDestroy {
    @Output() onLangChange: EventEmitter<string> = new EventEmitter<string>();

    private _lang: string = null;
    get lang(): string {
        return this._lang;
    }
    set lang(language: string) {
        this.param = !this.param;
        this._lang = language;
        this.onLangChange?.emit(language);
    }

    private _param: boolean = false;
    get param(): boolean {
        return this._param;
    }
    set param(param: boolean) {
        this._param = param;
    }
    private _activeLanguages: string[] = ['es', 'en', 'fr', 'pt'];
    get activeLanguages(): string[] {
        return this._activeLanguages;
    }
    set activeLanguages(value: string[]) {
        this._activeLanguages = value;
    }

    public static initTranslateSubscription(component: {
        _translate: TranslateService;
        _transParam?: boolean;
        translate?: Function;
        ngOnDestroy(): void;
    }): void {
        if (component.translate) {
            component.translate();
        }

        if (component._transParam === undefined) {
            component._transParam = false;
        }

        component._translate?.onLangChange.pipe(untilComponentDestroyed(component)).subscribe(() => {
            if (component._transParam !== undefined) {
                component._transParam = !component._transParam;
            }
            if (component.translate) {
                component.translate();
            }
        });
    }
    // inject our translations
    constructor(
        @Inject(TRANSLATIONS) private _translations: TranslationConfig[],
        private http: HttpClient) { }

    ngOnDestroy(): void { }

    /**
     * Method that sets the language of the platform
     * @param {string} language String indicating the language to set
     * @param {boolean} first Indicates if it is an initial set
     */
    async set(language: string, first?: boolean): Promise<void> {
        if (first) {
            this.lang = language;
        }
        // set current language
        if (!this._translations[language]) {
            await this.loadJSON(language);
        } else {
            this.lang = language;
            this.addMetaDescription();
        }
    }

    private translationTransform(translation: string[]) {
      let createTranslation = this._translations[this.lang];

        if (!createTranslation) {
            return '';
        }

        for (const value of translation) {
            createTranslation = createTranslation[value];
        }

        return createTranslation;
    }

    private translate(string: string, param?: { [key: string]: string }): string {
        let translationObject = string.split('.');
        let translation = this.translationTransform(translationObject);

        if (param) {
            for (const key of Object.keys(param)) {
                const regex = new RegExp(`\{{(\\s?${key}\\s?)\}}`, 'g');
                translation = translation.replace(regex, param[key]);
            }
        }

        return translation;
    }

    public massTranslate(list: any, key: string, tstring: string, obj: boolean = true, key2?: string): void {
        if (!obj) {
            for (let i = 0; i < list.length; i++) {
                list[i][key] = this.translate(tstring + key2);
            }
        } else {
            for (const li in list) {
                list[li][key] = this.translate(tstring + li);
            }
        }
    }

    public addRequiredLabel(list: any, key: string, tstring: string): void {
        for (const li in list) {
            list[li][key] = `${this.translate(tstring + li)} *`;
        }
    }

    public instant(label: string, param?: { [key: string]: string }): string {
        return this.translate(label, param);
    }

    private async loadJSON(language: string): Promise<void> {
        await this.loadSingleJSON(language);
        await this.loadDefaultLanguageJSON(language);
        this.lang = language;
        this.addMetaDescription();
    }

    async loadSingleJSON(language: string): Promise<void> {
        const response = await this.http.get(`assets/languages/${language}.json?v=${VERSION}`).toPromise();
        if (response) {
            this._translations[language] = response;
        }
    }

    async loadDefaultLanguageJSON(language: string): Promise<void> {
        try {
            const response = await this.http.get(`assets/languages-default/${language}.json?v=${VERSION}`).toPromise();
            if (response) {
                Object.keys(response).forEach(keyDefault => {
                    if (!(keyDefault in this._translations[language])) {
                        this._translations[language][keyDefault] = response[keyDefault];
                    };
                });
            }
        } catch (error) {
            console.error('Error loading default language JSON');
        }
    }

    /**
     * Adds the meta description to the html, based on language
     */
    private addMetaDescription(): void {
        if (!document.getElementById('meta-description')) {
            const meta = document.createElement('meta');
            meta.setAttribute('name', 'description');
            meta.setAttribute('id', 'meta-description');
            meta.setAttribute('content', this.translate('meta_description'));
            document.head.appendChild(meta);
        } else {
            const meta = document.getElementById('meta-description');
            meta.setAttribute('content', this.translate('meta_description'));
        }
    }
}
